// © Microsoft Corporation. All rights reserved.

import { canUseDOM, getDocumentElementsByTagName, getLocationSearch, getLocationHostname } from "@msnews/isomorphic-util";
import { applicationCacheConstants, clientSettings, dir, htmlElementIds, info, js, lang, pagetypeSettings } from "../constants";

import { AudienceModeType } from "@msnews/experiences-constants";
import { DeviceFormFactorType } from "cms-models-msn/configuration/types/DeviceFormFactorType";
import { Environment } from "./Environment";
import { ILocale } from "cms-models-msn/configuration/ILocale";
import { IPcsBrowserScope } from "../types/PcsBrowsersType";
import { MarketProperties } from "./MarketProperties";
import { WebClientData } from "./WebClientData";
import { OperatingSystemType } from "cms-models-msn/configuration/types/OperatingSystemType";
import { UrlUtility } from "../utilities/UrlUtility";
import { localStorage } from "../utilities/WebStorage";
import { tryParseJson } from "../utilities/Parsing";
import { KeyValueArray } from "../utilities";

/**
 * Format used for activity ID stored in storage
 */
export type StoredActivityId = {
    /**
     * ID value
     */
    id: string,

    /**
     * Timestamp of when value was stored
     */
    ts: number,

    /**
     * Flag inidicating if the ID was used in a previous page (stale)
     */
    previouslyUsed?: boolean
};

/**
 * JSON data contract / interface for settings in the <head> element that are part of the data-client-settings attribute.
 */
export interface IClientSettings {
    aid: string,
    apptype: string,
    apps_locale: string,
    audienceMode?: AudienceModeType,
    browser?: IPcsBrowserScope,
    configRootUrl?: string,
    deviceFormFactor?: DeviceFormFactorType,
    domain?: string,
    env: Environment,
    featureFlags?: any,
    contentId?: string,

    /** The schema://domain to be used for the main feed URL */
    feedBaseDomain?: string,

    categoryKey?: string,
    functionalonly_cookie_experience: boolean,
    functional_cookie_patterns: string,
    functional_cookies: string,
    geo_city?: string,
    geo_country?: string,
    geo_countryname?: string;
    geo_lat: string,
    geo_long: string,
    geo_subdivision?: string,
    geo_zip?: string,
    locale?: ILocale,
    market?: MarketProperties,
    os?: OperatingSystemType,
    pagetype?: string,

    /** Query params in the format "?key=value&..." as observed from the PCS origin server */
    queryparams?: string,

    static_page?: string,
    verticalKey?: string,

    /** cache busting identifier sent by pcs */
    cbid?: string

    /** Peregrine Build version sent by PCS */
    v?: string
}

export const EnterpiseFlightId = "prg-enterpriseblended-t";
export const OfficeDefaultPivotFlightId = "prg-officedefault-t";

/**
 * Class for getting the head data information.
 */
class HeadData {
    /**
     * The single instance of the HeadData class.
     * @type {HeadData}
     */
    private static _instance: HeadData;

    private anonCookieName: string;
    private locale: string;
    private marketDir: string;
    private currentFlights: string;
    private clientSettings: IClientSettings;
    private enableCMSPPEConfigs: boolean = false;
    private userIsSignedIn: boolean; // Always signed out on server side: https://dev.azure.com/microsoft/OS/_workitems/edit/25344027
    private isPreload: boolean;
    private headElement: any;
    private htmlElement: any;
    private isAppcache: boolean;
    private isEnterprise: boolean;
    private isOfficeDefaultPivot: boolean;
    private isServiceWorkerCache: boolean;
    private ssoComplete: boolean;
    private aadState: boolean;

    private locationSearch: string;

    // TODO: Task 24890741: refactor this class to use the current singleton pattern with ES6 modules
    // This will also clean up these getters/setters
    public get Locale(): string { return this.locale; }
    public get MarketDir(): string { return this.marketDir; }
    public get CurrentFlights(): string { return this.currentFlights || ""; }
    public get ClientSettings(): IClientSettings { return this.clientSettings; }
    public get EnableCMSPPEConfigs(): boolean { return this.enableCMSPPEConfigs; }
    public get UserIsSignedIn(): boolean { return this.userIsSignedIn; }
    public get IsPreload(): boolean { return this.isPreload; }
    public get IsAppCache(): boolean { return this.isAppcache; }
    public get IsEnterprise(): boolean { return this.isEnterprise; }
    public get IsOfficeDefaultPivot(): boolean { return this.isOfficeDefaultPivot; }
    public get IsServiceWorkerCache(): boolean { return this.isServiceWorkerCache; }

    // TODO: Dependencies that some client side code expects headData to specify but doesn't due to PCS
    public get AnonCookieName(): string { return this.anonCookieName; }
    public get SsoComplete(): boolean { return this.ssoComplete; }
    public get AadState(): boolean { return this.aadState; }

    // For testing purposes add setters
    public set AnonCookieName(value: string) { this.anonCookieName = value; }
    public set LocaleName(value: string) { this.locale = value; }
    public set MarketDir(value: string) { this.marketDir = value; }
    public set CurrentFlights(value: string) { this.currentFlights = value; }
    public set ClientSettings(value: IClientSettings) { this.clientSettings = value; }
    public set IsEnterprise(value: boolean) { this.isEnterprise = value; }
    public set IsOfficeDefaultPivot(value: boolean) { this.isOfficeDefaultPivot = value; }
    public set UserIsSignedIn(value: boolean) { this.userIsSignedIn = value; }

    /**
     * Initialize the head data instance, on server side provide the client info & client settings
     * @param {WebClientData} clientData Data object containing both client info and settings
     */
    private constructor(clientData?: WebClientData) {
        this.initializeHeadData(clientData);
    }

    /**
     * Returns singleton class instance.
     * @returns {HeadData}
     */
    public static getInstance(): HeadData { return HeadData._instance || (HeadData._instance = new HeadData()); }

    /**
     * Reset singleton class instance with passed in info and client settings. Used on server side to reset HeadData for each request.
     * @param {WebClientData} clientData Data object containing both client info and settings
     * @returns {HeadData}
     */
    public static resetInstance(clientData?: WebClientData): HeadData {
        HeadData._instance = new HeadData(clientData);
        headData = HeadData._instance;
        return HeadData._instance;
    }

    /**
     * Initializes head data. If WebClientData is provided use that, otherwise do client side initialization.
     * @param {WebClientData} clientData - Data object containing both client info and settings
     */
    public initializeHeadData(clientData?: WebClientData): void {
        if (clientData) {
            // Data is passed in only from Render Service under Node.JS environment since there is no DOM.
            this.initializeFromData(clientData);
            return;
        }

        this.locationSearch = getLocationSearch();
        this.htmlElement = canUseDOM() ? getDocumentElementsByTagName(htmlElementIds.html)[0] : null;
        this.headElement = canUseDOM() ? getDocumentElementsByTagName(htmlElementIds.head)[0] : null;

        if (!this.htmlElement || !this.headElement) {
            return;
        }

        // We always initialize from DOM first because
        // even if we are in an appcache/service worker case, we still need some data from the DOM
        // and we will fallback to DOM if there are no items found in storage
        this.initializeFromDom();

        // Attempt to override with local storage item when in appcache scenario
        // In service worker scenario, just update the storage activity id
        if (this.htmlElement.getAttribute(applicationCacheConstants.manifestAttributeName)) {
            this.isAppcache = true;
            this.initializeFromStorage();
        } else if (window && window.navigator && window.navigator.serviceWorker && window.navigator.serviceWorker.controller) {
            this.isServiceWorkerCache = true;
            this.updateStorageActivityId();
        }

        // Below items are common for both scenarios
        const language = this.htmlElement.getAttribute(lang);
        this.locale = language && language.toLowerCase() || "";

        const marketDir = this.htmlElement.getAttribute(dir);
        this.marketDir = marketDir && marketDir.toLocaleLowerCase() || "";

        this.setQueryItemProvidedValues();

        // Note: This isn't currently being stamped by PCS
        let headDataJs = this.headElement.getAttribute(js);
        if (headDataJs) {
            const headJs = <{ anonckname: string, signedin: number }>tryParseJson(headDataJs);
            this.anonCookieName = headJs.anonckname;
            this.userIsSignedIn = headJs.signedin === 1;
        }

        // Checks if current request is background preload request
        if (UrlUtility.getQueryParameterByName("backgroundpreload", this.locationSearch)) {
            this.isPreload = true;
        }

        // Determine if we are in Enterprise version or not
        this.setIsEnterprise();

        // Determine if Office is the default pivot
        this.setIsOfficeDefaultPivot();
    }

    /**
     * Helper that gets the current's page stored last used activityID (if there is one)
     */
    public getCurrentPageTypeStoredActivityId(): StoredActivityId {
        const storedActivities = localStorage.getItem(applicationCacheConstants.storedLastUsedActivityIdKey) || "";
        const storedActivityIds = tryParseJson(storedActivities);

        return (storedActivityIds && storedActivityIds[headData.ClientSettings.pagetype]) || null;
    }

    /**
     * Initializes the client settings and info from client data passed in externally (render service query param)
     * @param {WebClientData} clientData Data object containing both client info and settings
     */
    private initializeFromData(clientData: WebClientData): void {
        this.clientSettings = <IClientSettings>tryParseJson(clientData.clientSettings);

        this.setFlights(clientData.clientInfo);

        this.setQueryItemProvidedValues();

        this.locale = clientData.language && clientData.language.toLowerCase() || "";
        this.marketDir = clientData.marketDir && clientData.marketDir.toLocaleLowerCase() || "";

        // Determine if we are in Enterprise version or not
        this.setIsEnterprise();

        // Determine if Office is the default pivot
        this.setIsOfficeDefaultPivot();
    }

    /**
     * Initializes the client settings and info from DOM element
     */
    private initializeFromDom(): void {
        let dataClientSettings = this.headElement.getAttribute(clientSettings);
        if (dataClientSettings) {
            this.clientSettings = <IClientSettings>tryParseJson(dataClientSettings);

            // If clientSettings.pagetype does not exist, try getting from data-pagetype.
            // For Peregrine full spa page: pagetype is part of data-client-settings. There is no data-pagetype.
            // For Prime/Hybrid, pagetype is outside of data-client-settings and it belongs to data-pagetype
            if (!this.clientSettings.pagetype) {
                const pageTypeFromSettings = this.headElement.getAttribute(pagetypeSettings);
                this.clientSettings.pagetype = pageTypeFromSettings === null ? undefined : pageTypeFromSettings;
            }
        }
        this.setFlights(this.headElement.getAttribute(info));
    }

    /**
     * Initializes headData client settings and info from the value found in storage
     * and if none is found, fallbacks to using the DOM element
     */
    private initializeFromStorage(): void {
        const responseFromStorage = localStorage.getItem(applicationCacheConstants.storedHeadDataKey);
        const parsedResponseFromStorage = tryParseJson(responseFromStorage);

        // If there is nothing store, then fallback to the DOM
        if (!parsedResponseFromStorage) {
            return;
        }

        const currentPageStoredHeadData = parsedResponseFromStorage[this.clientSettings.pagetype];

        // We don't have an entry for the current page
        if (!currentPageStoredHeadData) {
            return;
        }

        const storedClientSettings = currentPageStoredHeadData["clientSettings"];
        const storedFlights = currentPageStoredHeadData["info"];

        // The stored head data is corrupt so we fallback to DOM
        if (!storedClientSettings || !storedFlights) {
            return;
        }

        this.clientSettings = storedClientSettings;
        this.setFlights(storedFlights);
        this.updateStorageActivityId();
    }

    /**
     * Update the last used activity ID in storage
     */
    private updateStorageActivityId(): void {
        if (!this.clientSettings.aid) {
            return;
        }

        const existingLastUsedActivityIds = tryParseJson(localStorage.getItem(applicationCacheConstants.storedLastUsedActivityIdKey)) || {};
        const currentPageLastUsedActivityId: StoredActivityId = existingLastUsedActivityIds[this.clientSettings.pagetype];

        // If the current pages last used activity ID matches the one for the current page head data
        // then we have re-used this. Mark it as such but don't override the timestamp
        if (currentPageLastUsedActivityId && (currentPageLastUsedActivityId.id === this.clientSettings.aid)) {
            currentPageLastUsedActivityId.previouslyUsed = true;
            existingLastUsedActivityIds[this.clientSettings.pagetype] = currentPageLastUsedActivityId;
        } else {
            // If they don't match then that means we haven't used this so store the new value.
            const activityIdToStore: StoredActivityId = { id: this.clientSettings.aid, ts: (new Date()).getTime() };
            existingLastUsedActivityIds[this.clientSettings.pagetype] = activityIdToStore;
        }

        // Update the last used activity ID in storage
        localStorage.setItem(applicationCacheConstants.storedLastUsedActivityIdKey, JSON.stringify(existingLastUsedActivityIds));
    }

    /**
     * Parses flights from provided string (if in proper format of f:{flights})
     * and sets it to the headData object
     */
    private setFlights(flightString: string): void {
        // The comma separated list of flights enabled in current request
        this.currentFlights = ((/f:\s*([^;]+)/i.exec(flightString) || {})[1] || "").toLowerCase();
    }

    /**
     * Determines if we are in Enterprise version based on clientSettings and currentFlights
     */
    private setIsEnterprise(): void {
        // We know we are in the Enterpise version of the page when both the audience mode is set to Enterprise
        // and the correct flight id is set.
        this.IsEnterprise = this.clientSettings &&
                            this.currentFlights &&
                            this.currentFlights.includes(EnterpiseFlightId) &&
                            this.clientSettings.audienceMode === AudienceModeType.Enterprise;
    }

    /**
     * Read query items to determine the headdata variable values.
     */
    private setQueryItemProvidedValues(): void {
        const queryItems = UrlUtility.getParamsWithItems(getLocationSearch());
        const cmsItem = queryItems.find(item => item.key === "cms");
        if (cmsItem && cmsItem.value === "ppe") {
            this.enableCMSPPEConfigs = true;
        }
        
        // Test override to add flights for scope purposes
        this.setCurrentFlightsTestOverride(queryItems);
    }

    /**
     * Overrides currentFlights when hostname is localhost. Used for testing.
     */
    private setCurrentFlightsTestOverride(queryItems: KeyValueArray<string, string>): void {
        if (getLocationHostname().toLowerCase().includes("localhost") && queryItems) {
            const flights = queryItems.find(item => item.key === "flights");
            if (flights) {
                this.currentFlights = flights.value;
            }
        }
    }

    /**
     * Determines if Office is the default pivot based on flight id.
     */
    private setIsOfficeDefaultPivot(): void {
        // There is work scheduled to have "sticky" pivots stored in some form of local storage which will
        // replace this check. Until then, the only way to know if Office is the default pivot is to check
        // for this flight id
        this.IsOfficeDefaultPivot = this.currentFlights && this.currentFlights.includes(OfficeDefaultPivotFlightId);
    }
}

export const getHeadData = HeadData.getInstance;

// headData can be reinitialized through resetHeadData.
export let headData = HeadData.getInstance();

/**
 * Export function to reset headData based on WebClientData
 */
export const resetHeadData = HeadData.resetInstance;